import type { ParticipantRow, ParticipantTask } from './participants';

export type ParticipantSortKey =
  | 'trusted'
  | 'name'
  | 'signedOff'
  | 'inReview'
  | 'averageReviewMs'
  | 'averageWritingMs'
  | 'revisionRatio';
export type TaskSortKey =
  | 'id'
  | 'writer'
  | 'status'
  | 'reviewMs'
  | 'progressMs'
  | 'sinceLastActivityMs';
export interface ActivitySort<Key extends string> {
  key: Key;
  direction: 'ascending' | 'descending';
}

export function nextActivitySort<Key extends string>(
  sort: ActivitySort<Key>,
  key: Key,
  numeric: boolean,
): ActivitySort<Key> {
  return {
    key,
    direction:
      sort.key === key
        ? sort.direction === 'ascending'
          ? 'descending'
          : 'ascending'
        : numeric
          ? 'descending'
          : 'ascending',
  };
}

function compare(
  a: string | number | null,
  b: string | number | null,
  direction: ActivitySort<string>['direction'],
): number {
  // Missing averages/ratios stay last in either direction; zero is a real value.
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  const result =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'ascending' ? result : -result;
}

export function sortParticipants(
  rows: ParticipantRow[],
  sort: ActivitySort<ParticipantSortKey>,
): ParticipantRow[] {
  return [...rows].sort(
    (a, b) =>
      compare(
        sort.key === 'trusted' ? Number(a.trusted === true) : a[sort.key],
        sort.key === 'trusted' ? Number(b.trusted === true) : b[sort.key],
        sort.direction,
      ) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}

export function sortParticipantTasks(
  tasks: ParticipantTask[],
  sort: ActivitySort<TaskSortKey>,
): ParticipantTask[] {
  const value = (item: ParticipantTask): string | number | null => {
    switch (sort.key) {
      case 'id':
        return item.task.id;
      case 'writer':
        return item.task.ownerName || item.task.ownerEmail || item.task.ownerId || 'Unassigned';
      case 'status':
        return item.task.status;
      default:
        return item[sort.key];
    }
  };
  return [...tasks].sort(
    (a, b) => compare(value(a), value(b), sort.direction) || a.task.id.localeCompare(b.task.id),
  );
}
